// components/scans/ScansTable.jsx
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../hooks/useRTL';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    IconButton,
    Chip,
    Box,
    TablePagination,
    CircularProgress,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Restore as RestoreIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';

const ScansTable = ({
    scans,
    stockItems,
    canEdit,
    canDelete,
    isRadiologist,
    isRepresentative,
    onEdit,
    onDelete,
    // Pagination props
    page,
    rowsPerPage,
    total,
    onChangePage,
    onChangeRowsPerPage,
    loading,
    restoreScan
}) => {
    const { t } = useTranslation();
    const { isRTL } = useRTL();
    const navigate = useNavigate();
    const { userType, user } = useAuth();

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ar-EG', {
            style: 'currency',
            currency: 'EGP',
        }).format(amount);
    };

    const formatDate = (date) => {
        return format(new Date(date), 'MMM dd, yyyy');
    };

    const renderBranchPricing = (scan) => {
        // For radiologists, don't show pricing
        if (userType === 'radiologist') {
            return null;
        }

        // For superAdmin, show all branches with prices
        if (userType === 'superAdmin') {
            return (
                <Box>
                    {scan.branchPricing?.map((bp, index) => (
                        <Box key={bp._id} sx={{ mb: index < scan.branchPricing.length - 1 ? 0.5 : 0 }}>
                            <Typography variant="caption" display="block">
                                <strong>{bp.branch?.name}:</strong> {formatCurrency(bp.price)}
                            </Typography>
                        </Box>
                    ))}
                    {(!scan.branchPricing || scan.branchPricing.length === 0) && (
                        <Typography variant="caption" color="textSecondary">
                            {t('scans.noBranchPricing') || 'No branch pricing'}
                        </Typography>
                    )}
                </Box>
            );
        }

        // For regular users, show only their assigned branch price
        const userBranchId = user?.branch?._id || user?.branchId;
        const userBranchPricing = scan.branchPricing?.find(bp => bp.branch?._id === userBranchId);

        if (userBranchPricing) {
            return (
                <Box>
                    <Typography variant="body2">
                        {formatCurrency(userBranchPricing.price)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {userBranchPricing.branch?.name}
                    </Typography>
                </Box>
            );
        }

        return (
            <Typography variant="caption" color="textSecondary">
                {t('scans.noPricingForBranch') || 'No pricing for your branch'}
            </Typography>
        );
    };

    return (
        <>
            <TableContainer component={Paper}>
                <Paper sx={{ mt: 2 }}>
                    <TableContainer dir={isRTL ? 'rtl' : 'ltr'}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align={isRTL ? 'right' : 'left'}>{t('scans.name')}</TableCell>
                                    <TableCell align={isRTL ? 'right' : 'left'}>{t('scans.category') || 'Category'}</TableCell>
                                    {!isRadiologist && !isRepresentative && (
                                        <>
                                            <TableCell align={isRTL ? 'right' : 'left'}>{t('scans.actualCost')}</TableCell>
                                            <TableCell align={isRTL ? 'right' : 'left'}>
                                                {userType === 'superAdmin'
                                                    ? t('scans.branchPricing') || 'Branch Pricing'
                                                    : t('scans.price') || 'Price'}
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell align={isRTL ? 'right' : 'left'}>{t('scans.items')}</TableCell>
                                    <TableCell align={isRTL ? 'right' : 'left'}>{t('common.status')}</TableCell>
                                    <TableCell align={isRTL ? 'right' : 'left'}>{t('scans.created')}</TableCell>
                                    <TableCell align={isRTL ? 'right' : 'left'}>{t('scans.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={isRadiologist || isRepresentative ? 6 : 8}
                                            align="center"
                                            sx={{ py: 4 }}
                                        >
                                            <CircularProgress size={40} />
                                        </TableCell>
                                    </TableRow>
                                ) : scans.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={isRadiologist || isRepresentative ? 6 : 8}
                                            align="center"
                                            sx={{ py: 4 }}
                                        >
                                            <Typography variant="body2" color="textSecondary">
                                                {t('scans.noScansFound') || 'No scans found'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    scans.map((scan) => (
                                        <TableRow key={scan._id} hover>
                                            <TableCell align={isRTL ? 'right' : 'left'}>
                                                <Typography variant="subtitle2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                    {scan.name}
                                                </Typography>
                                                {scan.description && (
                                                    <Typography variant="caption" color="textSecondary" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                        {scan.description}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align={isRTL ? 'right' : 'left'}>
                                                {scan.category ? (
                                                    <Chip
                                                        label={scan.category.name || 'Unknown Category'}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: scan.category.color || '#1976d2',
                                                            color: 'white'
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary">-</Typography>
                                                )}
                                            </TableCell>
                                            {!isRadiologist && !isRepresentative && (
                                                <>
                                                    <TableCell align={isRTL ? 'right' : 'left'}>
                                                        {formatCurrency(scan.actualCost || 0)}
                                                    </TableCell>
                                                    <TableCell align={isRTL ? 'right' : 'left'}>
                                                        {renderBranchPricing(scan)}
                                                    </TableCell>
                                                </>
                                            )}
                                            <TableCell align={isRTL ? 'right' : 'left'}>
                                                <Box>
                                                    {scan.items?.slice(0, 2).map((item, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={`${stockItems && stockItems.find(stock => stock._id === item.item)?.name || item.item} (${item.quantity})`}
                                                            size="small"
                                                            sx={{ mr: 0.5, mb: 0.5 }}
                                                        />
                                                    ))}
                                                    {scan.items?.length > 2 && (
                                                        <Chip
                                                            label={`+${scan.items.length - 2} ${t('scans.moreItems')}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell align={isRTL ? 'right' : 'left'}>
                                                <Chip
                                                    label={scan.isActive ? t('common.active') : t('common.inactive')}
                                                    color={scan.isActive ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align={isRTL ? 'right' : 'left'}>
                                                {formatDate(scan.createdAt)}
                                            </TableCell>
                                            <TableCell align={isRTL ? 'right' : 'left'}>
                                                {userType !== 'radiologist' && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/scans/${scan._id}`)}
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => canEdit && onEdit(scan)}
                                                    disabled={!canEdit}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                {canDelete && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onDelete(scan)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                                {!scan.isActive && userType !== 'radiologist' && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => restoreScan(scan)}
                                                        title={t('scans.restoreScan') || 'Restore Scan'}
                                                    >
                                                        <RestoreIcon />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </TableContainer>
            {/* Pagination Component */}
            <Paper sx={{ mt: 3 }}>

                <TablePagination
                    marginTop={2}
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={onChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    labelRowsPerPage={t('common.rowsPerPage') || 'Rows per page:'}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('common.of') || 'of'} ${count !== -1 ? count : `${t('common.moreThan') || 'more than'} ${to}`}`
                    }
                    showFirstButton
                    showLastButton
                    dir={isRTL ? 'rtl' : 'ltr'}
                    sx={{
                        '& .MuiTablePagination-toolbar': {
                            direction: isRTL ? 'rtl' : 'ltr',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            textAlign: isRTL ? 'right' : 'left',
                        }
                    }}
                />
            </Paper >
        </>
    );
};

export default ScansTable;